import { ReactNode } from "react"
import { formatDate } from "@/lib/format-date.ts"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { cn } from "@/lib/utils"
import { Language } from "@/types/__generated__/union-types"

export const PatientInfoHeader = ({
  lastName,
  firstName,
  children,
}: {
  lastName: string
  firstName: string
  children: ReactNode
}) => {
  const patientName = `${lastName}, ${firstName}`

  return (
    <div className="flex gap-1">
      <h1
        className={cn("text-on-surface truncate text-xl font-bold", "@sm:group-has-[>span]:pl-7 @xl:pl-0!")}
        data-testid="patient-name"
        title={patientName}>
        {patientName}
      </h1>
      {children}
    </div>
  )
}

const PatientInfoWrapper = (props: { children: ReactNode }) => {
  return (
    <>
      {props.children}
      <span>{" • "}</span>
    </>
  )
}

export const PatientInfo = ({
  birthDate,
  displayAge,
  secondaryLanguages,
  primaryLanguage,
  displayId,
}: {
  birthDate: string
  displayAge: string
  secondaryLanguages: readonly Language[]
  primaryLanguage: Language
  displayId: number
}) => {
  return (
    <>
      <PatientInfoWrapper>
        <span data-testid="patient-dob">{formatDate(new Date(birthDate))}</span>
        <span data-testid="patient-age">({displayAge})</span>
      </PatientInfoWrapper>
      <PatientInfoWrapper>
        <span data-testid="patient-primary-language">{convertLanguageToISOCode(primaryLanguage)}</span>
        {secondaryLanguages.length > 0 && (
          <span data-testid="patient-second-language">
            (
            {secondaryLanguages
              .filter((item) => item && item !== primaryLanguage)
              .map((item) => convertLanguageToISOCode(item))
              .sort()
              .join(", ")}
            )
          </span>
        )}
      </PatientInfoWrapper>
      {displayId && (
        <PatientInfoWrapper>
          <span data-testid="patient-display-id">{displayId}</span>
        </PatientInfoWrapper>
      )}
    </>
  )
}
